import { Anchor, Button, Text } from '@mantine/core';
import { t } from 'i18next';
import { useState } from 'react';

import { TwoFactorCodeError } from '@/auth/Errors';
import useAuth from '@/auth/useAuth';

import AuthFormLayout from './AuthFormLayout';
import { isCompleteTwoFactorCode, twoFactorErrorMessage } from '@/auth/twoFactor';

import { TwoFactorCodeInput } from './TwoFactorCodeInput';

type TwoFactorSignInFormProps = {
	twoFactorToken: string;
	email: string;
	onSignedIn: () => void;
	/** Goes back to the password form, for example after the token expired. */
	onCancel: () => void;
};

/** The second step of a password sign-in for an account with two-factor authentication. */
export function TwoFactorSignInForm({ twoFactorToken, email, onSignedIn, onCancel }: TwoFactorSignInFormProps) {
	const { completeTwoFactorSignIn } = useAuth();
	const [code, setCode] = useState('');
	const [error, setError] = useState<string>();
	const [isSubmitting, setIsSubmitting] = useState(false);

	const submit = async () => {
		if (!isCompleteTwoFactorCode(code) || isSubmitting) {
			return;
		}

		setIsSubmitting(true);
		setError(undefined);
		try {
			await completeTwoFactorSignIn(twoFactorToken, code.trim(), email);
			onSignedIn();
		} catch (err) {
			const type = err instanceof TwoFactorCodeError ? err.type : 'UNKNOWN';
			setError(twoFactorErrorMessage(type));
			setCode('');
			setIsSubmitting(false);
		}
	};

	return (
		<AuthFormLayout
			title={t('twoFactor.signIn.title')}
			onSubmit={event => {
				event.preventDefault();
				void submit();
			}}
			fields={
				<>
					<Text size="sm" c="dimmed">
						{t('twoFactor.signIn.description')}
					</Text>
					<TwoFactorCodeInput
						value={code}
						onChange={value => {
							setCode(value);
							setError(undefined);
						}}
						error={error}
						disabled={isSubmitting}
					/>
				</>
			}
			auxButton={
				<Anchor size="sm" component="button" type="button" onClick={onCancel} ta="center" c="dark">
					{t('twoFactor.signIn.back')}
				</Anchor>
			}
			actionButton={
				<Button type="submit" loading={isSubmitting} disabled={!isCompleteTwoFactorCode(code)}>
					{t('twoFactor.signIn.submit')}
				</Button>
			}
		/>
	);
}
